import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { cloneDeep, groupBy } from 'lodash';
import { FormLayout } from '@components/layout/form-layout';
import { BackgroundLayout } from '@components/layout/BackgroundLayout';
import { QuestionnaireGroup } from '@components/questionnaire/QuestionnaireGroup';
import { DynamicTypography } from '@components/DynamicTypography';
import {
  IncompleteSubmissionModal,
  UnansweredQuestion
} from '@components/IncompleteSubmissionModal';
import questionnaireService from '../services/QuestionnaireService';
import {
  FormGroup,
  FormValue,
  Question,
  QuestionnaireSelectorState,
  QuestionOption
} from '@app/types';
import { saveAnswers, setActiveTab, setDiscarded, setLoading, submitAnswers } from '@store/sagas/questionnaire';

interface FormValues {
  [key: string]: FormValue;
}

export const Questionnaire = () => {
  const { activeTab, tabs, answers } = useSelector(
    (state: QuestionnaireSelectorState) => state.app.form
  );
  const task = useSelector((state: QuestionnaireSelectorState) => state.app.task);
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [formGroups, setFormGroups] = useState<FormGroup[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setAllPageCount] = useState(0);
  const [changing, setChange] = useState(false);
  const [showOxfordTransition, setShowOxfordTransition] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [unansweredList, setUnansweredList] = useState<UnansweredQuestion[]>([]);
  const [isNavigatingUnanswered, setIsNavigatingUnanswered] = useState(false);
  const formRef = useRef<FormikProps<FormValues>>(null);

  const getQuestions = () => {
    return questionnaireService.prepareQuestions(
      1,
      formGroups
        ?.map((formGroup) =>
          formGroup?.data
            ?.flat() // flatten groups (pages)
            ?.map((option) => {
              const { layout, appends, actions, ...props } = option as Question;
              if (appends?.length) {
                return [
                  props,
                  appends.map((append) => {
                    return {
                      group: props.group,
                      key: append.option?.questionKey,
                      type: append.option?.type,
                      title: append.option?.label,
                      condition: append.rule
                    };
                  })
                ].flat(); // flatten appends into same level with main question
              }

              return props;
            })
            // flatten questions with appends
            ?.flat()
        )
        ?.flat()
        ?.filter((valid) => valid) as Question[],
      formRef?.current?.values as FormValues
    );
  };

  const getUnansweredQuestions = (overrideValues?: FormValues): UnansweredQuestion[] => {
    const unanswered: UnansweredQuestion[] = [];
    let questionNumber = 0;
    const values = overrideValues || formRef?.current?.values || {};

    formGroups.forEach((group, pageIndex) => {
      const visibleQuestions = questionnaireService.prepareQuestions(
        1,
        (group.data?.[0] || []) as Question[],
        { ...values }
      );

      visibleQuestions.forEach((question: Question) => {
        if (question.type === 'hidden' || question.optional) return;
        if (
          question.ignore &&
          questionnaireService.evaluateQuestion(question.ignore as string | boolean, values)
        )
          return;

        questionNumber++;
        const value = values[question.key];
        const isUnanswered =
          value === undefined ||
          value === null ||
          value === '' ||
          (Array.isArray(value) && value.length === 0);
        if (isUnanswered) {
          unanswered.push({ questionKey: question.key, questionNumber, pageIndex });
        }
      });
    });

    return unanswered;
  };

  const getTotalVisibleQuestions = (): number => {
    let count = 0;
    const values = formRef?.current?.values || {};
    formGroups.forEach((group) => {
      const visibleQuestions = questionnaireService.prepareQuestions(
        1,
        (group.data?.[0] || []) as Question[],
        { ...values }
      );
      visibleQuestions.forEach((question: Question) => {
        if (question.type === 'hidden' || question.optional) return;
        if (
          question.ignore &&
          questionnaireService.evaluateQuestion(question.ignore as string | boolean, values)
        )
          return;
        count++;
      });
    });
    return count;
  };

  const generateValidation = () => {
    const questions = getQuestions();
    const values = formRef?.current?.values || {};
    const shape: Yup.ObjectShape = {};

    if (!questions?.length) return Yup.object();

    questions.forEach((question: Question) => {
      shape[question.key] = Yup.lazy((val) => {
        const isArray = Array.isArray(val);
        let validation = isArray ? Yup.array() : Yup.string();
        const availableOptions = question?.options?.filter(
          (option: QuestionOption) =>
            !option.disabled ||
            (option.disabled && !questionnaireService.evaluateQuestion(option.disabled, values))
        );

        if (question?.optional || (question.type !== 'text' && !availableOptions?.length)) {
          return validation.nullable();
        }

        let rule: unknown;
        if (isArray) {
          rule = Yup.array().of(Yup.string());
          if (question?.minOptions) {
            rule = (rule as Yup.StringSchema).min(
              questionnaireService.evaluateQuestion(
                question.minOptions as unknown as string,
                values
              ) as unknown as number,
              `${question.title} ${t('common:validation.isMandatory')}`
            );
          } else {
            rule = (rule as Yup.StringSchema).min(
              1,
              `${question.title} ${t('common:validation.isMandatory')}`
            );
          }
        } else {
          rule = Yup.string().required(`${question.title} ${t('common:validation.isMandatory')}`);
        }

        if ((question?.validationGroup?.length ?? 0) > 0) {
          validation = (
            validation as Yup.ArraySchema<unknown[] | undefined, Yup.AnyObject, undefined, ''>
          ).when(question.validationGroup as string[], (props) => {
            return props.filter((propVal) => !!propVal).length < 1
              ? (rule as Yup.StringSchema)
              : Yup.string().nullable();
          });
        } else {
          validation = rule as Yup.StringSchema;
        }

        if (question?.type === 'number') {
          rule = Yup.number().required();
          if (question.minValue || question.minValue === 0) {
            rule = (rule as Yup.StringSchema).min(question.minValue);
          }

          if (question.maxValue || question.maxValue === 0) {
            rule = (rule as Yup.StringSchema).max(question.maxValue);
          }

          validation = rule as Yup.StringSchema;
        }

        return validation;
      });

      if ((question?.validationRules?.length ?? 0) > 0) {
        for (const validationRule of question.validationRules ?? []) {
          shape[validationRule.then] = Yup.string().when(validationRule.when, (value, schema) => {
            if ((value as unknown as string) === validationRule.includes) {
              return Yup.string().required();
            }
            return schema;
          });
        }
      }
    });

    return Yup.object().shape(shape);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, []);

  useEffect(() => {
    if (activeTab) {
      let groups = getFormGroups();
      const localAnswers = getValueswithDefaults(
        ((answers && answers[activeTab as keyof typeof answers]) || {}) as FormValues,
        groups
      );
      formRef.current?.setValues(localAnswers);
      // remove groups without any question on them
      // might not work for dynamic questionnaires such as QR
      groups = groups.filter((gr) => {
        return (
          questionnaireService.prepareQuestions(
            1,
            gr.data?.[0] as unknown as Question[],
            localAnswers
          ).length > 0
        );
      });
      setFormGroups(groups as FormGroup[]);
      setAllPageCount(groups.length);
    } else {
      navigate(`/questionnaire/${params.token}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const getValueswithDefaults = (evaluateValues?: FormValues, evaluateGroups?: FormGroup[]) => {
    const values = JSON.parse(JSON.stringify(evaluateValues || formRef?.current?.values));
    const questionsWithDefaults = cloneDeep(evaluateGroups || formGroups)
      .map((formGroup: FormGroup) => formGroup.data.flat())
      .flat()
      .filter((question) => question?.default);
    if (questionsWithDefaults.length > 0) {
      for (const question of questionsWithDefaults) {
        if (!values[question?.key ?? ''] || values[question?.key ?? ''].length < 1) {
          if (
            question?.ignore &&
            questionnaireService.evaluateQuestion(question?.ignore as boolean, values)
          ) {
            delete values[question.key];
          } else {
            values[question?.key ?? ''] = question?.default;
          }
        }
      }
    }

    // inject PRE_POST_OPERATION question in ANSWER
    const PRE_POST_OPERATION = task?.patients[0].parameters.find(
      (p) => p.name === 'PRE_POST_OPERATION'
    );
    if (PRE_POST_OPERATION != null) {
      values.PRE_POST_OPERATION = parseInt(PRE_POST_OPERATION.value, 10);
    }

    return values;
  };

  const onNext = async () => {
    if (currentPage < formGroups.length - 1) {
      setChange(true);
      setTimeout(() => {
        setCurrentPage(currentPage + 1);
        setChange(false);
      }, 650);
    } else {
      const formRoutes = formRouter();
      const currentTabIndex = formRoutes.map((tab) => tab.key).indexOf(activeTab ?? '');
      const isLastTab =
        formRoutes.find((formRoute) => formRoute.key !== 'overview') &&
        currentTabIndex === formRoutes.length - 1;

      if (isLastTab) {
        const unanswered = getUnansweredQuestions();
        if (unanswered.length > 0) {
          const currentType = getQuestionaireType(activeTab ?? '');
          if (['oss', 'oks', 'ohs'].includes(currentType ?? '')) {
            setUnansweredList(unanswered);
            setShowIncompleteModal(true);
          }
          // MDS/EQ forms are required — do not allow incomplete submission
          return;
        }
        dispatch(saveAnswers({ [activeTab as string]: getValueswithDefaults() }));
        setTimeout(() => {
          dispatch(submitAnswers());
        }, 300);
        return;
      }

      const nextTab = formRoutes[currentTabIndex + 1];

      const currentType = activeTab?.split('_')[3];
      const nextType = nextTab.key?.split('_')[3];
      const isCurrentEQ = currentType === 'eq';
      const isNextOxford = nextType === 'ohs' || nextType === 'oks' || nextType === 'oss';

      if (isCurrentEQ && isNextOxford) {
        dispatch(saveAnswers({ [activeTab as string]: getValueswithDefaults() }));
        setShowOxfordTransition(true);
        return;
      }

      dispatch(saveAnswers({ [activeTab as string]: getValueswithDefaults() }));
      setCurrentPage(0);
      dispatch(setActiveTab(nextTab.key));
      setTimeout(() => {
        setCurrentPage(0);
      }, 50);
    }
  };

  const onBack = () => {
    if (isNavigatingUnanswered) {
      setIsNavigatingUnanswered(false);
    }

    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    } else {
      const formRoutes = formRouter();
      const formIndex = formRoutes.map((tab) => tab.key).indexOf(activeTab ?? '');
      if (formIndex <= 0) {
        navigate(-1);
      } else {
        const nextActiveTab = formRoutes[formIndex - 1].key;
        setCurrentPage(0);
        dispatch(setActiveTab(nextActiveTab));
        formRef.current?.setValues(
          (answers?.[nextActiveTab as keyof typeof answers] || {}) as FormValues
        );
        setTimeout(() => {
          if ((getFormGroups(nextActiveTab).length || 1) - 1 > 0) {
            setCurrentPage((getFormGroups(nextActiveTab).length || 1) - 1);
          }
        }, 0);
      }
    }
  };

  const formRouter = () => {
    return [
      ...(tabs ?? []),
      ...(tabs && !tabs[0].skipOverview
        ? [
            {
              key: 'overview',
              title: t('common:overview.title')
            }
          ]
        : [])
    ];
  };

  const formRoutes = formRouter();
  const formIndex = formRoutes.map((tab) => tab.key).indexOf(activeTab ?? '');
  const totalFormPages = formRoutes?.length;

  const navigateToNextUnanswered = (remaining: UnansweredQuestion[]) => {
    if (remaining.length === 0) {
      setIsNavigatingUnanswered(false);
      setCurrentPage(formGroups.length - 1);
      return;
    }

    const nextOnDifferentPage = remaining.find((q) => q.pageIndex !== currentPage);
    if (nextOnDifferentPage) {
      setCurrentPage(nextOnDifferentPage.pageIndex);
    }
  };

  const onConfirmSubmit = () => {
    setShowIncompleteModal(false);
    dispatch(saveAnswers({ [activeTab as string]: getValueswithDefaults() }));
    setTimeout(() => {
      dispatch(submitAnswers({ incomplete: true }));
    }, 300);
  };

  const onGoToUnanswered = () => {
    setShowIncompleteModal(false);
    setIsNavigatingUnanswered(true);
    if (unansweredList.length > 0) {
      setCurrentPage(unansweredList[0].pageIndex);
    }
  };

  const onDiscardForm = () => {
    setShowIncompleteModal(false);
    dispatch(setDiscarded(true));
    dispatch(setLoading(false));
    navigate('/questionnaire/submit', { replace: true });
  };

  const getQuestionaireType = (activeTab: string) => {
    return tabs?.find((qt) => qt.key === activeTab)?.type;
  };

  const getFormGroups = (sActive?: string): FormGroup[] => {
    let localActiveTab = activeTab ?? '';
    if (sActive) {
      localActiveTab = sActive;
    }

    const [joint] = localActiveTab.split('_');
    const questionnaireType = getQuestionaireType(localActiveTab);
    let questionnaire = t(`${questionnaireType}:joints.${joint}`, {
      returnObjects: true
    });

    // Check if this is an Oxford form
    const [, , , type] = localActiveTab.split('_');
    const isOxford = type === 'ohs' || type === 'oks' || type === 'oss';

    if (isOxford) {
      const questionnaireArray = Array.isArray(questionnaire)
        ? questionnaire
        : Object.values(questionnaire);
      const hiddenQuestions = questionnaireArray.filter((item: Question) => item.type === 'hidden');
      const oxfordQuestions = questionnaireArray.filter(
        (item: Question) => item.type === 'eq_type_1'
      );

      const parsedGroups = oxfordQuestions.map((question: Question, index: number) => {
        const questionsForPage = [...hiddenQuestions, question];
        return {
          title: '',
          description: question.title,
          phaseKey: `${question.group}_Q${index + 1}`,
          data: [questionsForPage]
        };
      });

      setAllPageCount(parsedGroups.length);
      return parsedGroups as FormGroup[];
    }

    // For non-Oxford forms, use the original logic
    const groups = groupBy(questionnaire, (item: Question) => item.group);
    const parsedGroups = (
      t(`${questionnaireType}:phases`, {
        returnObjects: true
      }) as Question[]
    )
      ?.map((phase) => {
        return {
          title: phase.title,
          description: phase.description,
          phaseKey: phase.key,
          data: groups[phase.key] ? [groups[phase.key]] : undefined
        };
      })
      ?.filter((group) => (group?.data?.length ?? 0) > 0);

    setAllPageCount(parsedGroups.length);
    return parsedGroups as FormGroup[];
  };

  const onSubmit = (
    data: FormValues,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmitting(false);
  };

  const isValid = (): boolean => {
    const currentType = getQuestionaireType(activeTab ?? '');
    if (['oss', 'oks', 'ohs'].includes(currentType ?? '')) {
      return true;
    }

    const currentGroup = formGroups?.[currentPage]?.data?.[0]?.filter(
      (item: Question) => !item.ignore
    );

    if (!formRef?.current?.values || !currentGroup) {
      return false;
    }

    return currentGroup.every(
      (currentQuestion: Question) => formRef.current?.values[currentQuestion.key]
    );
  };

  const getAnsweredPages = (): boolean[] => {
    const values = formRef?.current?.values || {};
    return formGroups.map((group) => {
      const questions = (group.data?.[0] || []) as Question[];
      const visible = questions.filter(
        (q: Question) => q.type !== 'hidden' && !q.optional && !q.ignore
      );
      if (visible.length === 0) return true;
      return visible.every((q: Question) => {
        const val = values[q.key];
        return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
      });
    });
  };

  const onGoToPage = (page: number) => {
    if (page >= 0 && page < formGroups.length) {
      const currentType = getQuestionaireType(activeTab ?? '');
      const isOxford = ['oss', 'oks', 'ohs'].includes(currentType ?? '');
      // For non-Oxford, only allow navigating to answered pages or going back
      if (!isOxford && page > currentPage && !isValid()) {
        return;
      }
      setCurrentPage(page);
    }
  };

  if (!activeTab) {
    return null;
  }

  const RenderSection = ({
    setFieldValue,
    values,
    errors,
    validateField
  }: {
    setFieldValue: (k: string, v: FormValue) => void;
    values: FormValues;
    errors: Record<string, boolean>;
    validateField: (k: string) => void;
  }) => {
    const section = formGroups[currentPage];
    const item = section.data[0] ?? [];
    let replace = '';
    if (tabs?.find((tab) => tab.key === activeTab)?.type === 'eq') {
      const joint = tabs.find((tab) => tab.key === activeTab);
      const [, jointSide] = joint?.key.split('_') || [];
      replace = t(`eq:placeholders.${jointSide.toUpperCase()}_${joint?.joint?.toUpperCase()}`);
    }

    return (
      <div style={{ minHeight: 100, width: '100%' }}>
        <div className={'sectionSeparator'}>
          {section.title && <p className={'groupTitle'}>{section.title}</p>}
          {section.description && section.description.length > 0 && (
            <DynamicTypography
              className={'groupDescription'}
              content={section.description.replace(/\[placeholder\]/g, replace)}
            />
          )}
        </div>
        <div className={'questionnaire-container'}>
          <QuestionnaireGroup
            questions={item}
            setFieldValue={setFieldValue}
            validateField={validateField}
            values={values}
            errors={errors}
          />
        </div>
      </div>
    );
  };

  const onSetValue = (
    k: string,
    v: FormValue,
    setFieldValue: (k: string, v: FormValue) => void
  ) => {
    setFieldValue(k, v);

    // check if all questions answered on this section
    const localValues = {
      ...formRef?.current?.values,
      [k]: v
    };

    if (isNavigatingUnanswered && ['BACK_PAIN', 'HEALTH_VALUE', 'SHOULDER_PAIN'].indexOf(k) < 0) {
      const remaining = getUnansweredQuestions(localValues);
      if (remaining.length === 0) {
        setIsNavigatingUnanswered(false);
        setCurrentPage(formGroups.length - 1);
      } else {
        setTimeout(() => {
          navigateToNextUnanswered(remaining);
        }, 400);
      }
      return;
    }

    const allQuestionsAnswered = formGroups[currentPage]?.data?.[0]?.every(
      (q: Question) => q.ignore || Boolean(localValues[q.key])
    );
    const formRoutes = formRouter();

    const isLastTab = formRoutes.map((tab) => tab.key).indexOf(activeTab) === formRoutes.length - 1;
    const isLastPageInTab = currentPage === totalPages - 1;
    const isLastQuestionInEntireForm = isLastTab && isLastPageInTab;

    if (
      allQuestionsAnswered &&
      !isLastQuestionInEntireForm &&
      ['BACK_PAIN', 'HEALTH_VALUE', 'SHOULDER_PAIN'].indexOf(k) < 0
    ) {
      setTimeout(onNext, 400);
    }
  };

  const getOxfordTitle = () => {
    if (!tabs || tabs.length === 0) return t('common:oxfordTransition.defaultTitle');

    const formRoutes = formRouter();
    const currentIndex = formRoutes.map((tab) => tab.key).indexOf(activeTab ?? '');
    const nextTab = formRoutes[currentIndex + 1];

    if (!nextTab) return t('common:oxfordTransition.defaultTitle');

    const nextType = nextTab.key?.split('_')[3];

    switch (nextType) {
      case 'oks':
        return t('common:oksHeader');
      case 'ohs':
        return t('common:ohsHeader');
      case 'oss':
        return t('common:ossHeader');
      default:
        return t('common:oxfordTransition.defaultTitle');
    }
  };

  const handleOxfordTransitionContinue = () => {
    const formRoutes = formRouter();
    const nextTab = formRoutes[formRoutes.map((tab) => tab.key).indexOf(activeTab ?? '') + 1];

    setShowOxfordTransition(false);
    setCurrentPage(0);
    dispatch(setActiveTab(nextTab.key));
    setTimeout(() => {
      setCurrentPage(0);
    }, 50);
  };

  const handleOxfordTransitionBack = () => {
    setShowOxfordTransition(false);
  };

  const [, , , questionnaireType] = activeTab.split('_');

  if (showOxfordTransition) {
    return (
      <BackgroundLayout
        innerStyle="padding-left: 0; padding-right: 0; padding-top: 0;"
        transparent={true}
        showIcon={false}>
        <div className="oxford-transition-container">
          <div className="oxford-header">
            <h1 className="oxford-title">{getOxfordTitle()}</h1>
          </div>

          <div className="oxford-content">
            <div className="oxford-message">
              <DynamicTypography content={t('common:oxfordTransition.message')} />
            </div>
          </div>

          <div className="oxford-footer">
            <div className="oxford-footer-container">
              <button
                onClick={handleOxfordTransitionContinue}
                className="oxford-nav-button oxford-nav-next"
                type="button">
                <span className="oxford-nav-label">{t('common:questionnaire.next')}</span>
                <div className="oxford-nav-icon oxford-nav-icon-active">
                  <img src={require('../assets/icons/right-icon.png')} alt="" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </BackgroundLayout>
    );
  }

  return (
    <Formik
      initialValues={(answers && answers[activeTab as keyof typeof answers]) || {}}
      validationSchema={generateValidation}
      onSubmit={onSubmit}
      validateOnChange={false}
      validateOnBlur={false}
      innerRef={formRef}>
      {({ setFieldValue, values, errors, validateField }) => (
        <>
          <FormLayout
            nextLabel={t('common:questionnaire.next')}
            submitLabel={t('common:questionnaire.submit')}
            prevLabel={t('common:questionnaire.prev')}
            onBack={onBack}
            onNext={onNext}
            state={changing}
            activeTab={activeTab}
            currentPage={currentPage}
            totalPages={totalPages}
            formIndex={formIndex}
            totalFormPages={totalFormPages}
            isValid={isValid()}
            questionnaireType={questionnaireType}
            hideBackButton={questionnaireType !== 'eq' && currentPage === 0}
            answeredPages={getAnsweredPages()}
            onGoToPage={onGoToPage}>
            {activeTab !== 'overview' && formGroups?.length && (
              <RenderSection
                setFieldValue={(k, v) => onSetValue(k, v, setFieldValue)}
                values={values}
                errors={errors}
                validateField={validateField}
              />
            )}
          </FormLayout>
          <IncompleteSubmissionModal
            show={showIncompleteModal}
            unansweredQuestions={unansweredList}
            totalQuestions={getTotalVisibleQuestions()}
            onConfirm={onConfirmSubmit}
            onGoToUnanswered={onGoToUnanswered}
            onDiscard={onDiscardForm}
          />
        </>
      )}
    </Formik>
  );
};
